import { useMemo, useState } from 'react';
import { layoutFlow } from '../types';
import type { FlowDefinition } from '../types';

const NODE_W = 186;
const NODE_H = 54;
const GAP_X = 26;
const GAP_Y = 46;

/** How many steps sit side by side before a rank wraps onto another line. */
const PER_RANK = 5;

/** A colour per node type, so the shape of a flow is readable before any label is. */
const TONE: Record<string, string> = {
  message: 'var(--color-brand)',
  list: '#7C5BD6',
  buttons: '#7C5BD6',
  capture: '#B7791F',
  condition: '#0E7490',
  action: '#B45309',
  dynamic_list: '#B45309',
  set: 'var(--color-muted)',
  handoff: '#BE185D',
  end: 'var(--color-muted)',
};

/**
 * The flow as a picture.
 *
 * For the one question a list of sixty-four cards cannot answer: what shape is this,
 * and what is nothing pointing at? Laid out in columns by distance from the start,
 * which is how people read a flow — a force-directed graph of the same nodes is a
 * cloud.
 *
 * Read-only by design. Dragging boxes to connect them is the expensive half of a
 * canvas — hit-testing, edge routing, undo — and it produces exactly what the "goes
 * to" dropdown in the node editor already produces, less reliably and not at all on a
 * phone. Clicking a node opens it there.
 */
export function FlowMap({ definition, selected, onSelect }: {
  definition: FlowDefinition;
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const [zoom, setZoom] = useState(1);
  const { nodes, edges } = useMemo(() => layoutFlow(definition), [definition]);

  // Where each rank starts down the page, given that an earlier one may have wrapped
  // onto several lines. Computed once rather than per node, because a rank's top
  // depends on every rank above it.
  const { rankTop, totalLines } = useMemo(() => {
    const perRank = new Map<number, number>();
    for (const node of nodes) perRank.set(node.column, Math.max(perRank.get(node.column) ?? 0, node.row + 1));

    const tops = new Map<number, number>();
    let line = 0;
    for (const rank of [...perRank.keys()].sort((a, b) => a - b)) {
      tops.set(rank, line * (NODE_H + GAP_Y));
      line += Math.ceil((perRank.get(rank) ?? 1) / PER_RANK);
    }
    return { rankTop: tops, totalLines: line };
  }, [nodes]);

  const positions = useMemo(() => {
    const map = new Map<string, { x: number; y: number }>();
    for (const node of nodes) {
      // `column` is distance from the start and `row` is the position among the
      // steps at that distance — so depth goes DOWN the page and siblings spread
      // across it. The names come from the layout helper and are left alone; what
      // they are mapped onto is this file's business.
      //
      // Wide ranks wrap. One rank of this flow holds thirteen steps — the seven
      // `set_qt_*` branches of the ticket menu and their neighbours — and a straight
      // reading would be 2,700px across, which is the sideways scrolling this layout
      // was changed to get rid of, reappearing in the other axis.
      const line = Math.floor(node.row / PER_RANK);
      map.set(node.id, {
        x: (node.row % PER_RANK) * (NODE_W + GAP_X),
        y: (rankTop.get(node.column) ?? 0) + line * (NODE_H + GAP_Y),
      });
    }
    return map;
  }, [nodes, rankTop]);

  const widest = Math.min(PER_RANK, Math.max(1, ...nodes.map((n) => n.row + 1)));
  const width = widest * (NODE_W + GAP_X);
  const height = Math.max(1, totalLines) * (NODE_H + GAP_Y);

  if (nodes.length === 0) {
    return (
      <p className="rounded border border-dashed border-(--color-line) px-4 py-10 text-center text-small text-(--color-muted)">
        No steps yet. Add one under Steps and it appears here.
      </p>
    );
  }

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <span className="text-small text-(--color-muted)">
          {nodes.length} steps, {edges.length} connections
          {nodes.some((n) => n.unreachable) &&
            ` · ${nodes.filter((n) => n.unreachable).length} unreachable`}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {([0.6, 0.8, 1] as const).map((level) => (
            <button
              key={level}
              type="button"
              onClick={() => setZoom(level)}
              className={[
                'rounded px-2 py-0.5 text-small transition',
                zoom === level ? 'bg-(--color-brand-soft) text-(--color-brand)' : 'text-(--color-muted)',
              ].join(' ')}
            >
              {Math.round(level * 100)}%
            </button>
          ))}
        </div>
      </div>

      {/* Bounded, and it scrolls inside itself. Unbounded it drew a box as tall as
          the whole flow with the page's own scrollbar as the only way through it,
          which put the tabs and the live test off the top of the screen. */}
      <div className="pane-scroll max-h-[68vh] overflow-auto rounded-lg border border-(--color-line) bg-(--color-paper) p-4">
        <svg
          width={width * zoom}
          height={height * zoom}
          viewBox={`-8 -8 ${width + 16} ${height + 16}`}
          role="img"
          aria-label="The flow as a diagram"
        >
          <defs>
            <marker id="flow-arrow" viewBox="0 0 8 8" refX="7" refY="4"
              markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M0 0 L8 4 L0 8 z" fill="var(--color-line)" />
            </marker>
          </defs>

          {edges.map((edge, i) => {
            const from = positions.get(edge.from);
            const to = positions.get(edge.to);
            if (!from || !to) return null;

            // Out of the bottom, into the top.
            const x1 = from.x + NODE_W / 2;
            const y1 = from.y + NODE_H;
            const x2 = to.x + NODE_W / 2;
            const y2 = to.y;
            const mid = (y1 + y2) / 2;

            // A cubic through the midpoint: straight lines between ranks cross each
            // other into an unreadable mesh, and a curve separates them.
            return (
              <path
                key={`${edge.from}-${edge.to}-${i}`}
                data-from={edge.from}
                data-to={edge.to}
                d={`M ${x1} ${y1} C ${x1} ${mid}, ${x2} ${mid}, ${x2} ${y2}`}
                fill="none"
                stroke="var(--color-line)"
                strokeWidth={1.2}
                markerEnd="url(#flow-arrow)"
                opacity={selected && edge.from !== selected && edge.to !== selected ? 0.25 : 1}
              />
            );
          })}

          {nodes.map((node) => {
            const at = positions.get(node.id)!;
            const tone = TONE[node.node.type ?? ''] ?? 'var(--color-muted)';
            const isSelected = node.id === selected;

            return (
              <g
                key={node.id}
                data-node={node.id}
                data-depth={node.column}
                data-unreachable={node.unreachable ? 'yes' : 'no'}
                transform={`translate(${at.x} ${at.y})`}
                onClick={() => onSelect(node.id)}
                className="cursor-pointer"
              >
                <rect
                  width={NODE_W} height={NODE_H} rx={7}
                  fill="var(--color-surface)"
                  stroke={isSelected ? 'var(--color-brand)' : node.unreachable ? 'var(--color-warn)' : 'var(--color-line)'}
                  strokeWidth={isSelected ? 2 : node.unreachable ? 1.5 : 1}
                  strokeDasharray={node.unreachable ? '4 3' : undefined}
                />
                <rect width={NODE_W} height={4} rx={2} fill={tone} />
                <text x={12} y={26} fontSize={13} fill="var(--color-ink)" fontWeight={500}>
                  {node.id.length > 22 ? node.id.slice(0, 21) + '…' : node.id}
                </text>
                <text x={12} y={43} fontSize={11.5} fill="var(--color-muted)">
                  {node.node.type ?? 'no type'}
                  {node.unreachable ? ' · unreachable' : ''}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <p className="mt-2 text-small text-(--color-muted)">
        Click a step to edit it. The map is for reading &mdash; connections are set on
        the step itself, where they can only point at somewhere that exists.
      </p>
    </div>
  );
}
